import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Factory, Wrench, Layers, Users, TrendingUp, Award, MapPin, Building2, FileText, HardHat } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const SERVICE_ICONS = [Factory, Wrench, Layers, HardHat];

const Home = () => {
  const [clients, setClients] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [services, setServices] = useState([]);

  useEffect(() => {
    axios.get(`${API}/clients?limit=6`).then(res => setClients(res.data)).catch(() => {});
    axios.get(`${API}/credentials`).then(res => setCredentials(res.data)).catch(() => {});
    axios.get(`${API}/homepage-services`).then(res => setServices(res.data)).catch(() => {});
  }, []);

  const stats = [
    { icon: Users, label: 'Skilled Workforce', value: '500+' },
    { icon: TrendingUp, label: 'Fabrication Capacity', value: '500 MT/Month' },
    { icon: Award, label: 'Years Experience', value: '19+' },
    { icon: Layers, label: 'Projects Executed', value: '25000+ MT' },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1761425158919-05b73baf6c61?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwc3RlZWwlMjBjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwd2VsZGluZyUyMGZhYnJpY2F0aW9uJTIwY3JhbmV8ZW58MHx8fHwxNzcyMTc2NDQ4fDA&ixlib=rb-4.1.0&q=85)'
          }}
        />
        <div className="absolute inset-0 bg-slate-900/80" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="max-w-3xl">
              <div className="inline-block border-l-4 border-orange-500 pl-4 mb-6">
                <p className="text-orange-500 font-oswald tracking-widest text-sm">SINCE 2007</p>
              </div>
              <h1 className="font-oswald font-bold text-5xl md:text-7xl leading-tight mb-6" data-testid="hero-title">
                PRECISION STRUCTURAL FABRICATION & INDUSTRIAL ERECTION EXPERTS
              </h1>
              <p className="text-lg md:text-xl mb-8 text-slate-300">
                Delivering heavy industrial steel structures with strength, safety, and reliability across India.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/projects"
                  data-testid="view-projects-btn"
                  className="bg-orange-500 hover:bg-orange-600 text-white font-bold font-oswald px-8 py-4 inline-flex items-center justify-center space-x-2 transition-all shadow-lg"
                >
                  <span>VIEW PROJECTS</span>
                  <ArrowRight size={20} />
                </Link>
                <Link
                  to="/quotation"
                  data-testid="submit-project-details-btn"
                  className="border-2 border-white text-white hover:bg-white hover:text-slate-900 font-bold font-oswald px-8 py-4 inline-flex items-center justify-center transition-all"
                >
                  SUBMIT PROJECT DETAILS
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center border-l-4 border-orange-500 pl-4"
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <stat.icon className="w-10 h-10 text-orange-500 mx-auto mb-3" />
                <div className="font-oswald font-bold text-3xl md:text-4xl text-slate-900 mb-1">{stat.value}</div>
                <div className="text-sm text-slate-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      {services.length > 0 && (
        <section className="py-24 bg-slate-50" data-testid="services-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-oswald font-bold text-4xl md:text-5xl text-slate-900 mb-4">OUR CORE SERVICES</h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Comprehensive mechanical construction and structural steel solutions for industrial clients
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service, index) => {
                const IconComponent = SERVICE_ICONS[index] || Factory;
                return (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="group bg-white border-l-4 border-orange-500 shadow-sm hover:shadow-xl transition-all overflow-hidden"
                    data-testid={`service-card-${index}`}
                  >
                    <div className="h-48 overflow-hidden">
                      {service.image ? (
                        <img
                          src={service.image}
                          alt={service.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full bg-slate-200 flex items-center justify-center">
                          <IconComponent className="w-16 h-16 text-slate-400" />
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <IconComponent className="w-10 h-10 text-orange-500 mb-3" />
                      <h3 className="font-oswald font-bold text-lg text-slate-900 mb-2">{service.title}</h3>
                      <p className="text-sm text-slate-600">{service.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/services"
                data-testid="view-all-services-btn"
                className="inline-flex items-center space-x-2 text-orange-500 font-oswald font-bold hover:text-orange-600 transition-colors"
              >
                <span>VIEW ALL SERVICES</span>
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Top Clients Section */}
      {clients.length > 0 && (
        <section className="py-20 bg-white" data-testid="clients-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-oswald font-bold text-4xl md:text-5xl text-slate-900 mb-4">OUR TOP CLIENTS</h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Trusted by leading industrial companies across India
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
              {clients.map((client, index) => (
                <motion.div
                  key={client.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08 }}
                  className="bg-slate-50 p-6 flex flex-col items-center text-center hover:shadow-lg transition-shadow border border-slate-200 group"
                  data-testid={`client-card-${index}`}
                >
                  {client.logo_url ? (
                    <div className="w-20 h-20 mb-4 flex items-center justify-center">
                      <img
                        src={client.logo_url}
                        alt={client.company_name}
                        className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform"
                      />
                    </div>
                  ) : (
                    <div className="w-20 h-20 mb-4 bg-slate-200 flex items-center justify-center">
                      <Building2 className="w-10 h-10 text-slate-400" />
                    </div>
                  )}
                  <h3 className="font-oswald font-bold text-sm text-slate-900 leading-tight">{client.company_name}</h3>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Credentials Section */}
      {credentials.length > 0 && (
        <section className="py-20 bg-slate-50" data-testid="home-credentials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-oswald font-bold text-4xl md:text-5xl text-slate-900 mb-4">OUR CREDENTIALS</h2>
              <p className="text-slate-600 max-w-2xl mx-auto">
                Proven track record with major industrial clients
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {credentials.slice(0, 6).map((cred, index) => (
                <motion.div
                  key={cred.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white border-l-4 border-orange-500 shadow-sm hover:shadow-xl transition-all overflow-hidden group"
                  data-testid={`home-credential-${index}`}
                >
                  {cred.certificate_image ? (
                    <div className="h-48 overflow-hidden bg-slate-100">
                      <img
                        src={cred.certificate_image}
                        alt={cred.company_name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  ) : (
                    <div className="h-48 bg-slate-100 flex items-center justify-center">
                      <FileText className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  <div className="p-6">
                    <h3 className="font-oswald font-bold text-xl text-slate-900 mb-2 group-hover:text-orange-500 transition-colors">
                      {cred.company_name}
                    </h3>
                    <div className="flex items-center text-sm text-slate-600">
                      <MapPin className="w-4 h-4 text-orange-500 mr-1 flex-shrink-0" />
                      <span>{cred.location}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/credentials"
                data-testid="view-all-credentials-btn"
                className="inline-flex items-center space-x-2 text-orange-500 font-oswald font-bold hover:text-orange-600 transition-colors"
              >
                <span>VIEW ALL CREDENTIALS</span>
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-24 bg-slate-900 text-white" data-testid="cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-oswald font-bold text-4xl md:text-5xl mb-6">READY TO START YOUR PROJECT?</h2>
          <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">
            Get a detailed techno-commercial quotation from our engineering team
          </p>
          <Link
            to="/quotation"
            data-testid="cta-get-quote-btn"
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold font-oswald px-10 py-4 inline-flex items-center space-x-2 transition-all shadow-lg"
          >
            <span>GET QUOTATION NOW</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;